import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';

interface FormattedHelpTextProps {
  text: string;
}

interface CollapsibleExampleProps {
  content: string[];
  maxVisibleLines?: number;
  title: string;
}

interface CollapsibleExampleProps {
  content: string[];
  maxVisibleLines?: number;
  title: string;
}

const CollapsibleExample: React.FC<CollapsibleExampleProps> = ({ 
  content, 
  maxVisibleLines = 0,
  title 
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const lines = content.filter(line => line.trim() !== '');
  const shouldCollapse = lines.length > maxVisibleLines;

  // Only show first maxVisibleLines lines initially
  const displayedLines = shouldCollapse ? lines.slice(0, maxVisibleLines) : lines;
  
  return (
    <>
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 font-mono text-sm">
          {displayedLines.map((line, i) => (
            <div key={i} className="text-green-700 whitespace-pre-wrap">
              {line}
            </div>
          ))}
          {shouldCollapse && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Show more...
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="overflow-hidden p-0">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 font-mono text-sm">
              {lines.map((line, i) => (
                <div key={i} className="text-green-700 whitespace-pre-wrap">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// export default CollapsibleExample;

const FormattedHelpText: React.FC<FormattedHelpTextProps> = ({ text }) => {
  const lines = text.split('\n');
  const sections: { type: string; content: string[] }[] = [];
  let currentSection = '';
  let currentContent: string[] = [];

  const addCurrentSection = () => {
    if (currentContent.length > 0) {
      sections.push({
        type: currentSection,
        content: [...currentContent]
      });
      currentContent = [];
    }
  };

  lines.forEach(line => {
    // Check if this is a section header
    if (['Example:', 'Examples:', 'Best practices:', 'Additional examples:', 'Avoid:', 'Notes:'].includes(line)) {
      addCurrentSection();
      currentSection = line;
    } else if (line.trim() === '') {
      // Empty line might indicate section end
      if (currentContent.length > 0) {
        addCurrentSection();
      }
    } else if (line.trim()) {
      // If we don't have a current section, treat as regular content
      if (!currentSection) {
        sections.push({ type: 'regular', content: [line] });
      } else {
        currentContent.push(line);
      }
    }
  });

  // Add any remaining content
  addCurrentSection();

  return (
    <div className="space-y-4">
      {sections.map((section, index) => {
        const hasMarkers = section.content.some(line => 
          line.startsWith('✓') || line.startsWith('✗') || line.startsWith('•')
        );

        // Plain text examples
        if ((section.type === 'Example:' || section.type === 'Examples:') && !hasMarkers) {
          return (
            <div key={index} className="space-y-4">
              <h3 className="font-medium text-gray-900">{section.type}</h3>
              <CollapsibleExample 
                content={section.content} 
                title={`${section.type.slice(0, -1)} Details`}
                maxVisibleLines={1}
              />
            </div>
          );
        }

        // Notes section
        if (section.type === 'Notes:') {
          return (
            <div key={index} className="space-y-1">
              <h3 className="font-medium text-gray-900">{section.type}</h3>
              {section.content.map((line, i) => {
                if (line.startsWith('•')) {
                  return (
                    <div key={i} className="flex items-start space-x-2 pl-4">
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-700">{line.substring(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }

        // Good examples
        if ((section.type === 'Examples:' || section.type === 'Additional examples:') && 
            section.content.some(line => line.startsWith('✓'))) {
          return (
            <div key={index} className="space-y-2">
              <h3 className="font-medium text-gray-900">{section.type}</h3>
              {section.content.map((line, i) => {
                if (line.startsWith('✓')) {
                  return (
                    <div key={i} className="flex items-start space-x-2 pl-4">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="text-gray-700">{line.substring(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }

        // Bad examples
        if (section.type === 'Avoid:') {
          return (
            <div key={index} className="space-y-2">
              <h3 className="font-medium text-gray-900">{section.type}</h3>
              {section.content.map((line, i) => {
                if (line.startsWith('✗')) {
                  return (
                    <div key={i} className="flex items-start space-x-2 pl-4">
                      <span className="text-red-600 font-bold">✗</span>
                      <span className="text-gray-700">{line.substring(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }

        // Best practices
        if (section.type === 'Best practices:') {
          return (
            <div key={index} className="space-y-2">
              <h3 className="font-medium text-gray-900">{section.type}</h3>
              {section.content.map((line, i) => {
                if (line.startsWith('•')) {
                  return (
                    <div key={i} className="flex items-start space-x-2 pl-4">
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-700">{line.substring(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }

        // Regular content
        if (section.type === 'regular') {
          return (
            <p key={index} className="text-gray-700">
              {section.content[0]}
            </p>
          );
        }

        return null;
      })}
    </div>
  );
};

export default FormattedHelpText;